'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Button } from '@/core/presentation/components/ui/Button';
import { Spinner } from '@/core/presentation/components/ui/Spinner';
import { Input } from '@/core/presentation/components/ui/Input';
import { OtpInput } from '@/core/presentation/components/ui/OtpInput';
import { formatPhoneWithCountryCode, normalizePhoneToNationalFormatted, parsePhoneInputToNational, toStoredPhoneWithCountryCode } from '@/core/utils/formatPhone';
import {
  formatPlanPriceBrlFromCents,
  PAYMENT_STATUS_LABELS,
  PLAN_BILLING_PERIOD_LABELS,
  SUBSCRIPTION_STATUS_PAYMENT_FAILED,
  SUBSCRIPTION_STATUS_PAST_DUE,
  SUBSCRIPTION_STATUS_PIX_MANUAL,
  userCanCancelStripeSubscriptionAtPeriodEnd,
} from '@/core/domain';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { MINHA_CONTA_BILLING } from '@/features/app/presentation/constants/minha-conta-billing';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import { useSubscription } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { useBillingInvoices } from '@/features/billing-invoices/presentation/context/BillingInvoicesContext';
import { usePlans } from '@/features/plans/presentation/context/PlansContext';
import { useToast } from '@/core/presentation/components/Toast';
import { usePayments } from '@/features/payments/presentation/context/PaymentsContext';
import { ConfirmModal } from '@/core/presentation/components/ui/ConfirmModal';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';
import { APP_SECTION_LOADING_MESSAGE } from '@/features/app/presentation/constants/app-shell-copy';

type PerfilData = {
  nome: string;
  email: string;
  telefone: string;
  empresa: string;
};

const EMPTY_PERFIL: PerfilData = {
  nome: '',
  email: '',
  telefone: '',
  empresa: '',
};

function formatDate(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTimeIso(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function planSubscriptionBadge(user: {
  subscription_status?: string | null;
  subscription_cancel_at_period_end?: boolean;
}): { label: string; className: string } {
  if (user.subscription_cancel_at_period_end) {
    return {
      label: MINHA_CONTA_BILLING.statusCancelScheduled,
      className: 'text-amber-800 bg-amber-50',
    };
  }
  const st = user.subscription_status ?? '';
  if (st === SUBSCRIPTION_STATUS_PAST_DUE) {
    return {
      label: MINHA_CONTA_BILLING.statusPastDue,
      className: 'text-amber-800 bg-amber-50',
    };
  }
  if (st === SUBSCRIPTION_STATUS_PAYMENT_FAILED) {
    return {
      label: MINHA_CONTA_BILLING.statusPaymentFailed,
      className: 'text-amber-800 bg-amber-50',
    };
  }
  if (st === SUBSCRIPTION_STATUS_PIX_MANUAL) {
    return {
      label: MINHA_CONTA_BILLING.statusPixManual,
      className: 'text-cyan-dark bg-cyan/10',
    };
  }
  if (st === 'pending') {
    return {
      label: MINHA_CONTA_BILLING.statusPending,
      className: 'text-text-muted bg-[#f3f4f6]',
    };
  }
  if (st === 'cancelled') {
    return {
      label: MINHA_CONTA_BILLING.statusCancelled,
      className: 'text-text-muted bg-[#f3f4f6]',
    };
  }
  return {
    label: MINHA_CONTA_BILLING.statusActive,
    className: 'text-green-700 bg-green-50',
  };
}

export interface MinhaContaSectionProps {
  /** Ao clicar em "Alterar plano", navegar para a seção de planos */
  onAlterarPlanoClick?: () => void;
}

function userToPerfil(name: string | null, email: string | null, phone: string, companyName: string | null): PerfilData {
  return {
    nome: name ?? '',
    email: email ?? '',
    telefone: normalizePhoneToNationalFormatted(phone),
    empresa: companyName ?? '',
  };
}

export const MinhaContaSection: React.FC<MinhaContaSectionProps> = ({
  onAlterarPlanoClick,
}) => {
  const { state: authState } = useAuth();
  const { state: userState, events: userEvents } = useUser();
  const { state: subscriptionState, events: subscriptionEvents } = useSubscription();
  const {
    state: billingState,
    events: billingEvents,
  } = useBillingInvoices();
  const { state: plansState, events: plansEvents } = usePlans();
  const { state: paymentsState, events: paymentsEvents } = usePayments();
  const toast = useToast();
  const currentUser = userState.currentUser;
  const subscription = subscriptionState.currentSubscription;
  const canCancelStripePlan =
    currentUser != null && userCanCancelStripeSubscriptionAtPeriodEnd(subscription);

  const loadMinhaContaSection = useCallback(async () => {
    const uid = authState.user?.uid;
    await Promise.all([
      plansEvents.refresh(),
      billingEvents.refresh({ forceRefresh: true }),
      uid ? subscriptionEvents.getSubscriptionByUserId(uid) : Promise.resolve(null),
    ]);
  }, [
    authState.user?.uid,
    plansEvents.refresh,
    billingEvents.refresh,
    subscriptionEvents.getSubscriptionByUserId,
  ]);

  const minhaContaDataReady = useAwaitInitialFetch(loadMinhaContaSection, [loadMinhaContaSection]);

  const perfilInicial = useMemo(
    () =>
      currentUser
        ? userToPerfil(currentUser.name, currentUser.email, currentUser.phone, currentUser.company_name)
        : EMPTY_PERFIL,
    [currentUser]
  );
  const [perfil, setPerfil] = useState<PerfilData>(perfilInicial);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState<PerfilData>(perfilInicial);
  const [phoneVerifyStep, setPhoneVerifyStep] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [cancelPlanModalOpen, setCancelPlanModalOpen] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setPerfil(perfilInicial);
      setEditForm(perfilInicial);
    }
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- sync when user loads

  const telefoneMudou = editingProfile && editForm.telefone !== perfil.telefone;
  const telefoneValido = editForm.telefone.replace(/\D/g, '').length >= 10;

  const activePlan =
    subscription?.plan_id ?
      plansState.plans.find((p) => p.id === subscription.plan_id)
    : undefined;
  const planName = activePlan?.title ?? MINHA_CONTA_BILLING.noPlanName;
  const renewalLine =
    subscription?.subscription_valid_until ?
      `${MINHA_CONTA_BILLING.renewalLabel} ${formatDateTimeIso(subscription.subscription_valid_until)}`
    : MINHA_CONTA_BILLING.renewalUnknown;
  const badge = subscription ? planSubscriptionBadge(subscription) : null;

  const cancelModalPlanPriceLine = useMemo(() => {
    if (!activePlan) return '—';
    if (activePlan.kind === 'free') {
      return `${formatPlanPriceBrlFromCents(0)} · Grátis`;
    }
    const period =
      activePlan.billing_variants?.monthly ? 'monthly'
      : activePlan.billing_variants?.semiannual ? 'semiannual'
      : activePlan.billing_variants?.annual ? 'annual'
      : null;
    const variant = period ? activePlan.billing_variants?.[period] : undefined;
    if (!period || !variant) return activePlan.title;
    return `${formatPlanPriceBrlFromCents(variant.price_brl_cents)} · ${PLAN_BILLING_PERIOD_LABELS[period]}`;
  }, [activePlan]);

  const cancelModalAccessUntil = useMemo(() => {
    const until = subscription?.subscription_valid_until?.trim();
    if (!until) return null;
    return formatDateTimeIso(until);
  }, [subscription?.subscription_valid_until]);

  const handleStartEditProfile = () => {
    userEvents.clearError();
    setEditForm(perfil);
    setEditingProfile(true);
    setPhoneVerifyStep(false);
    setVerifyCode('');
    setVerifyError('');
  };

  const handleCancelEditProfile = () => {
    setEditingProfile(false);
    setPhoneVerifyStep(false);
    setVerifyCode('');
    setVerifyError('');
  };

  const applySaveProfile = async (data: PerfilData) => {
    if (!currentUser) return;
    const phoneToStore = toStoredPhoneWithCountryCode(data.telefone) || currentUser.phone;
    const updated = await userEvents.updateUser(currentUser.id, {
      name: data.nome || null,
      email: data.email || null,
      phone: phoneToStore,
      company_name: data.empresa || null,
    });
    if (updated) {
      setPerfil(data);
      setEditingProfile(false);
      setPhoneVerifyStep(false);
      setVerifyCode('');
      toast.success('Perfil atualizado.');
    } else {
      toast.error(userState.error ?? 'Não foi possível atualizar o perfil.');
    }
  };

  const handleSaveProfileClick = () => {
    if (telefoneMudou && telefoneValido) {
      setPhoneVerifyStep(true);
      setVerifyCode('');
      setVerifyError('');
      return;
    }
    applySaveProfile(editForm);
  };

  const handleVerifyAndSaveProfile = () => {
    if (verifyCode.length !== 6) {
      setVerifyError('Digite o código de 6 dígitos.');
      return;
    }
    setVerifyError('');
    applySaveProfile(editForm);
  };

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm((prev) => ({ ...prev, telefone: parsePhoneInputToNational(e.target.value) }));
  };

  const handleConfirmCancelPlan = async (): Promise<boolean> => {
    if (!currentUser || !canCancelStripePlan) return false;
    paymentsEvents.clearError();
    const result = await paymentsEvents.cancelSubscription();
    if (result.success) {
      toast.success(MINHA_CONTA_BILLING.cancelPlanSuccess);
      void userEvents.getUserById(currentUser.id, { forceRefresh: true });
      void subscriptionEvents.getSubscriptionByUserId(currentUser.id);
      void billingEvents.refresh({ forceRefresh: true });
      return true;
    }
    toast.error(result.error);
    return false;
  };

  return (
    <SectionScreenLayout
      title="Minha conta"
      subtitle="Seus dados, plano e faturas."
    >
      {!minhaContaDataReady ? (
        <div className="flex items-center gap-2 text-text-muted text-sm py-10">
          <Spinner size="sm" className="text-cyan" aria-hidden />
          <span>{APP_SECTION_LOADING_MESSAGE}</span>
        </div>
      ) : (
      <div className="space-y-8">
        {/* Perfil */}
        <section>
          <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide mb-4 flex items-center gap-2">
            <AppIcon name="UserCircleIcon" size={18} className="text-cyan shrink-0" />
            Perfil
          </h2>
          <div className="rounded-xl border border-border-light bg-white p-4 sm:p-5 space-y-4">
            {!editingProfile ? (
              <>
                <p className="font-medium text-navy-dark">{perfil.nome}</p>
                <p className="text-sm text-text-muted flex items-center gap-2">
                  <AppIcon name="EnvelopeIcon" size={16} className="shrink-0" />
                  {perfil.email}
                </p>
                <p className="text-sm text-text-muted flex items-center gap-2">
                  <AppIcon name="PhoneIcon" size={16} className="shrink-0" />
                  {perfil.telefone ? formatPhoneWithCountryCode(perfil.telefone) : '—'}
                </p>
                <p className="text-sm text-text-muted flex items-center gap-2">
                  <AppIcon name="BuildingOfficeIcon" size={16} className="shrink-0" />
                  {perfil.empresa}
                </p>
                <div className="pt-2">
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={handleStartEditProfile}>
                    <AppIcon name="PencilSquareIcon" size={16} />
                    Editar perfil
                  </Button>
                </div>
              </>
            ) : !phoneVerifyStep ? (
              <>
                <Input
                  variant="light"
                  label="Nome"
                  value={editForm.nome}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, nome: e.target.value }))}
                  placeholder="Seu nome"
                />
                <Input
                  variant="light"
                  label="E-mail"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="seu@email.com"
                />
                <div>
                  <label
                    htmlFor="telefone-perfil"
                    className="block text-sm font-medium mb-1.5 text-navy-dark"
                  >
                    Telefone
                  </label>
                  <div className="flex rounded-lg border border-border-light bg-[#fafbfc] text-text-body focus-within:border-cyan focus-within:ring-1 focus-within:ring-cyan transition-colors">
                    <span className="pl-4 py-3 text-text-body shrink-0 select-none">+55 </span>
                    <input
                      id="telefone-perfil"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={editForm.telefone}
                      onChange={handleTelefoneChange}
                      placeholder="(00) 00000-0000"
                      className="flex-1 min-w-0 py-3 pr-4 bg-transparent border-0 focus:outline-none placeholder-text-muted"
                    />
                  </div>
                </div>
                <Input
                  variant="light"
                  label="Empresa"
                  value={editForm.empresa}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, empresa: e.target.value }))}
                  placeholder="Nome da empresa"
                />
                {userState.error && (
                  <p className="text-sm text-red-600 flex items-center gap-2">
                    <AppIcon name="ExclamationCircleIcon" size={18} className="shrink-0" />
                    {userState.error}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={handleCancelEditProfile} disabled={userState.loading}>
                    <AppIcon name="XMarkIcon" size={16} />
                    Descartar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="gap-1.5"
                    onClick={handleSaveProfileClick}
                    disabled={userState.loading}
                    loading={userState.loading}
                    loadingLabel="Salvando"
                  >
                    <AppIcon name="CheckIcon" size={16} />
                    Salvar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-text-muted">
                  Enviamos um código de verificação para <strong className="text-navy-dark">{editForm.telefone ? formatPhoneWithCountryCode(editForm.telefone) : editForm.telefone}</strong>. Digite o código abaixo para confirmar o novo número e salvar o perfil.
                </p>
                <OtpInput
                  value={verifyCode}
                  onChange={setVerifyCode}
                  variant="light"
                  error={verifyError}
                  aria-label="Código enviado por WhatsApp/SMS"
                />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setPhoneVerifyStep(false)} disabled={userState.loading}>
                    Voltar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="gap-1.5"
                    onClick={handleVerifyAndSaveProfile}
                    disabled={userState.loading}
                    loading={userState.loading}
                    loadingLabel="Salvando"
                  >
                    <AppIcon name="CheckIcon" size={16} />
                    Verificar e salvar
                  </Button>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Plano */}
        <section className="pt-4 border-t border-border-light">
          <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide mb-4 flex items-center gap-2">
            <AppIcon name="CreditCardIcon" size={18} className="text-cyan shrink-0" />
            {MINHA_CONTA_BILLING.planSectionTitle}
          </h2>
          <div className="rounded-xl border border-border-light bg-white p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-navy-dark">{planName}</p>
              {!subscription?.subscription_cancel_at_period_end ? (
                <p className="text-sm text-text-muted mt-0.5">{renewalLine}</p>
              ) : null}
              {badge ? (
                <span
                  className={`inline-flex items-start gap-1.5 mt-2 text-xs font-medium px-2 py-1.5 rounded-lg max-w-full ${badge.className}`}
                >
                  <AppIcon name="CheckCircleIcon" size={14} className="shrink-0 mt-0.5" />
                  <span className="text-left leading-snug">{badge.label}</span>
                </span>
              ) : null}
              {subscription?.subscription_cancel_at_period_end ? (
                <p className="text-sm text-text-muted mt-2">
                  {MINHA_CONTA_BILLING.planAccessUntilPrefix}{' '}
                  {subscription.subscription_valid_until?.trim() ?
                    formatDateTimeIso(subscription.subscription_valid_until)
                  : '—'}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={onAlterarPlanoClick}
            >
              <AppIcon name="ArrowPathIcon" size={16} />
              Alterar plano
            </Button>
          </div>
        </section>

        {/* Faturas */}
        <section className="pt-4 border-t border-border-light">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide flex items-center gap-2">
              <AppIcon name="DocumentTextIcon" size={18} className="text-cyan shrink-0" />
              {MINHA_CONTA_BILLING.invoicesSectionTitle}
            </h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-cyan text-xs"
              disabled={billingState.loading || !currentUser}
              onClick={() => void billingEvents.refresh({ forceRefresh: true })}
            >
              Atualizar lista
            </Button>
          </div>
          {billingState.error ? (
            <p className="text-sm text-red-600 mb-3">
              {MINHA_CONTA_BILLING.invoicesLoadError}{' '}
              <span className="font-medium text-red-800">{billingState.error}</span>
            </p>
          ) : null}
          <div className="rounded-xl border border-border-light bg-white overflow-hidden">
            {billingState.loading ? (
              <div className="px-4 py-6 flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-text-muted">
                <Spinner size="sm" className="text-cyan" aria-hidden />
                <span>Carregando faturas</span>
              </div>
            ) : billingState.payments.length === 0 ? (
              <p className="px-4 py-6 text-sm text-text-muted text-center">
                {MINHA_CONTA_BILLING.invoicesEmpty}
              </p>
            ) : (
              <ul className="divide-y divide-border-light">
                {billingState.payments.map((f) => (
                  <li
                    key={f.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4"
                  >
                    <div>
                      <p className="font-medium text-navy-dark">{formatDateTimeIso(f.created_at)}</p>
                      <p className="text-sm text-text-muted">
                        {formatPlanPriceBrlFromCents(f.amount_brl_cents)}
                      </p>
                      <p className="text-xs text-text-muted mt-0.5">
                        Período: {formatDate(f.billing_month)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                        {PAYMENT_STATUS_LABELS[f.status]}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="!text-cyan/50 !p-2 min-w-0 cursor-not-allowed"
                        disabled
                        title={MINHA_CONTA_BILLING.downloadUnavailable}
                      >
                        <AppIcon name="ArrowDownTrayIcon" size={18} aria-hidden />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Opções sensíveis: mais escondidas no final */}
        <section className="pt-8 border-t border-border-light">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
            Outras opções
          </p>
          <div className="rounded-xl border border-red-100 bg-red-50/50 p-4 sm:p-5 space-y-3">
            <p className="text-sm text-text-muted">
              Cancelar plano ou excluir conta. Os dados serão removidos de acordo com nossa política.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              {canCancelStripePlan ? (
                <>
                  <button
                    type="button"
                    className="text-sm text-amber-700 hover:text-amber-800 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded disabled:opacity-50 disabled:pointer-events-none"
                    disabled={paymentsState.loading}
                    onClick={() => setCancelPlanModalOpen(true)}
                  >
                    Cancelar plano
                  </button>
                  <span className="text-border-light">·</span>
                </>
              ) : null}
              <button
                type="button"
                className="text-sm text-red-600 hover:text-red-700 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded"
              >
                Excluir conta
              </button>
            </div>
          </div>
        </section>
      </div>
      )}

      <ConfirmModal
        isOpen={cancelPlanModalOpen}
        onClose={() => setCancelPlanModalOpen(false)}
        title={MINHA_CONTA_BILLING.cancelPlanModalTitle}
        cancelLabel={MINHA_CONTA_BILLING.cancelPlanModalBack}
        confirmLabel={MINHA_CONTA_BILLING.cancelPlanModalConfirm}
        confirmVariant="danger"
        onConfirm={handleConfirmCancelPlan}
      >
        <p className="text-sm text-text-muted mb-4">{MINHA_CONTA_BILLING.cancelPlanModalIntro}</p>
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="font-medium text-navy-dark">{MINHA_CONTA_BILLING.cancelPlanModalPlanLabel}</dt>
            <dd className="text-text-body mt-0.5">{planName}</dd>
          </div>
          <div>
            <dt className="font-medium text-navy-dark">{MINHA_CONTA_BILLING.cancelPlanModalPriceLabel}</dt>
            <dd className="text-text-body mt-0.5">{cancelModalPlanPriceLine}</dd>
          </div>
          <div>
            <dt className="font-medium text-navy-dark">{MINHA_CONTA_BILLING.cancelPlanModalAccessLabel}</dt>
            <dd className="text-text-body mt-0.5">
              {cancelModalAccessUntil ?? (
                <span className="text-text-muted">{MINHA_CONTA_BILLING.cancelPlanModalAccessFallback}</span>
              )}
            </dd>
          </div>
        </dl>
        <p className="text-sm text-text-muted mt-4 leading-relaxed">
          {MINHA_CONTA_BILLING.cancelPlanModalExplanation}
        </p>
      </ConfirmModal>
    </SectionScreenLayout>
  );
};
